'use client';

import React from 'react';
import { ArrowRight } from 'lucide-react';
import type { PostCardProps } from './post-card';
import { ABILITY_TYPE_CONFIG } from '@/lib/community/types';
import { stripMarkdown } from '@/lib/community/utils';
import { PostCardStats } from './post-card-stats';

const teamConfig = ABILITY_TYPE_CONFIG['agent-team'];

export const PostCardAgentTeam = React.memo(function PostCardAgentTeam({ post, onClick, onTagClick, onLike, onFavorite }: PostCardProps) {
	const members = post.teamMembers ?? [];
	const capCount = post.capabilities?.length ?? 0;

	return (
		<div
			className="liquid-glass-card rounded-md overflow-hidden cursor-pointer"
			onClick={(e) => {
				const card = e.currentTarget;
				onClick?.(post, card.getBoundingClientRect(), undefined);
			}}
		>
			<div className="p-3 flex flex-col gap-3 min-w-0">
				<div className="flex items-start justify-between gap-3">
					<div className="flex-1 min-w-0">
						<h3 className="font-semibold text-sm leading-snug line-clamp-2">{post.title}</h3>
						<p className="text-[11px] text-muted-foreground mt-0.5">
							{members.length}个智能体 · {capCount > 0 ? `${capCount}种能力组合` : '多能力协作'}
						</p>
					</div>
				</div>

				{members.length > 0 && (
					<div className="flex items-center gap-1 overflow-x-auto py-1 max-w-full">
						{members.map((member, idx) => (
							<div className="flex items-center gap-1 shrink-0" key={member.name}>
								<div className="flex flex-col items-center gap-0.5">
									<div className="relative w-7 h-7 rounded-full overflow-hidden bg-muted ring-2 ring-red-500/20">
										{member.avatar ? (
											<img alt={member.name} className="w-full h-full object-cover" src={member.avatar} />
										) : (
											<div
												className="w-full h-full"
												style={{
													background: `linear-gradient(135deg, ${teamConfig.hex}30, ${teamConfig.hexEnd ?? teamConfig.hex}30)`,
												}}
											/>
										)}
									</div>
									<span className="text-[10px] font-medium text-foreground truncate max-w-[48px]">{member.name}</span>
									<span className="text-[9px] text-muted-foreground truncate max-w-[48px]">{member.role}</span>
								</div>
								{idx < members.length - 1 && <ArrowRight className="w-3 h-3 text-red-400/60 shrink-0 mx-0.5" />}
							</div>
						))}
					</div>
				)}

				<p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed">{stripMarkdown(post.description)}</p>

				{post.tags.length > 0 && (
					<div className="flex flex-wrap gap-1">
						{post.tags.slice(0, 4).map((tag) => (
							<button
								className="inline-block rounded-full bg-secondary/70 text-foreground/70 px-2 py-0.5 text-[10px] hover:text-foreground hover:bg-secondary transition-colors cursor-pointer"
								key={tag}
								onClick={(e) => {
									e.stopPropagation();
									onTagClick?.(tag);
								}}
								type="button"
							>
								#{tag}
							</button>
						))}
					</div>
				)}

				<PostCardStats onFavorite={onFavorite} onLike={onLike} post={post} />
			</div>
		</div>
	);
});
